import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { FileText, Plus, Upload, Check, X, Lock, ListChecks, CheckCheck } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../lib/errorUtils';
import { useActiveCompany } from '../hooks/useActiveCompany';
import { currentFY, fyDropdownOptions } from '../lib/fyUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SECTIONS_80C = ['PPF', 'ELSS', 'LIC', 'EPF', 'SSY', 'NSC', 'FD_5yr', 'Home_Loan_Principal', 'Tuition_Fees', 'Others'];
const OTHER_SECTIONS = [
  { code: '80D', label: 'Medical Insurance', max: 100000 },
  { code: '80E', label: 'Education Loan Interest', max: null },
  { code: '80G', label: 'Donations', max: null },
  { code: '80TTA', label: 'Savings Interest', max: 10000 },
  { code: '80CCD', label: 'NPS (80CCD(1B))', max: 50000 },
  { code: '80EE', label: 'Home Loan Interest (First time)', max: 50000 },
];

const DeclarationProof = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [declarations, setDeclarations] = useState([]);
  const [activeDecl, setActiveDecl] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [fy, setFy] = useState(currentFY());
  const [regime, setRegime] = useState('new');
  const [sections, setSections] = useState([]);
  const [hra, setHra] = useState({ rent_paid_annual: 0, city_type: 'non_metro', landlord_name: '', landlord_pan: '' });
  const [hp, setHp] = useState({ interest_on_loan: 0, principal_repayment: 0 });
  const [otherIncome, setOtherIncome] = useState(0);
  const [view, setView] = useState('single');           // 'single' | 'bulk'
  const [allDecls, setAllDecls] = useState([]);
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkFilter, setBulkFilter] = useState('submitted'); // status filter for bulk view

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
      setCompanies(data); setSelectedCompany((cur) => (data.length > 0 && !cur ? data[0].id : cur));
    } catch (e) { console.error(e); }
  }, [setSelectedCompany]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);
  const fetchEmployees = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/employees?company_id=${selectedCompany}`, { withCredentials: true });
      setEmployees(data);
    } catch (e) { console.error(e); }
  }, [selectedCompany]);

  useEffect(() => { if (selectedCompany) fetchEmployees(); }, [selectedCompany, fetchEmployees]);
  const fetchDeclarations = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/statutory/declarations?employee_id=${selectedEmployee}&company_id=${selectedCompany}`, { withCredentials: true });
      setDeclarations(data);
      if (data.length > 0) loadDeclaration(data[0]);
    } catch (e) { console.error(e); }
  }, [selectedCompany, selectedEmployee]);

  useEffect(() => { if (selectedEmployee) fetchDeclarations(); }, [selectedEmployee, fetchDeclarations]);




  const loadDeclaration = (d) => {
    setActiveDecl(d);
    setFy(d.financial_year || currentFY());
    setRegime(d.regime || 'new');
    setSections(d.sections || []);
    setHra(d.hra || { rent_paid_annual: 0, city_type: 'non_metro' });
    setHp(d.house_property || { interest_on_loan: 0, principal_repayment: 0 });
    setOtherIncome(d.other_income || 0);
  };

  const handleSave = async () => {
    const payload = {
      employee_id: selectedEmployee, company_id: selectedCompany, financial_year: fy, regime, sections,
      hra, house_property: hp, other_income: otherIncome,
    };
    try {
      if (activeDecl) {
        await axios.patch(`${API_URL}/statutory/declarations/${activeDecl.id}`, payload, { withCredentials: true });
      } else {
        const { data } = await axios.post(`${API_URL}/statutory/declarations`, payload, { withCredentials: true });
        setActiveDecl(data);
      }
      fetchDeclarations();
      toast.success('Declaration saved');
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleAction = async (action) => {
    if (!activeDecl) return;
    try {
      await axios.post(`${API_URL}/statutory/declarations/${activeDecl.id}/${action}`, {}, { withCredentials: true });
      toast.success(`Declaration ${action}ed`);
      fetchDeclarations();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Bulk view — fetches ALL declarations for the company at the chosen status,
  // lets the HR admin batch-submit/verify/lock dozens of rows in a single click.

  const fetchAllDecls = useCallback(async (status = bulkFilter) => {
    if (!selectedCompany) return;
    try {
      const url = `${API_URL}/statutory/declarations?company_id=${selectedCompany}` + (status ? `&status=${status}` : '');
      const { data } = await axios.get(url, { withCredentials: true });
      setAllDecls(data || []);
      setBulkSelected(new Set());
    } catch (e) { toast.error(formatApiError(e)); }
  }, [selectedCompany, bulkFilter]);

  useEffect(() => { if (view === 'bulk') fetchAllDecls(); }, [view, fetchAllDecls]);

  const handleBulkAction = async (action) => {
    if (bulkSelected.size === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/statutory/declarations/bulk-action`,
        { declaration_ids: Array.from(bulkSelected), action },
        { withCredentials: true },
      );
      toast.success(data.message || `${data.modified_count} updated`);
      fetchAllDecls();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBulkBusy(false); }
  };

  const employeeName = (eid) => {
    const e = employees.find((x) => x.id === eid);
    return e ? `${e.personal_details?.first_name || ''} ${e.personal_details?.last_name || ''}`.trim() : eid;
  };
  const employeeCode = (eid) => employees.find((x) => x.id === eid)?.employment_details?.employee_code || '';

  const addSection = (section, subSection = '') => {
    setSections([...sections, { section, sub_section: subSection, declared_amount: 0, proof_submitted: false, proof_status: 'pending', approved_amount: 0, remarks: '' }]);
  };

  const updateSection = (idx, field, value) => {
    const updated = [...sections];
    updated[idx] = { ...updated[idx], [field]: value };
    setSections(updated);
  };

  const removeSection = (idx) => setSections(sections.filter((_, i) => i !== idx));

  const total80C = sections.filter(s => s.section === '80C').reduce((sum, s) => sum + (s.declared_amount || 0), 0);

  const statusColors = { draft: 'bg-slate-100 text-slate-600', submitted: 'bg-blue-100 text-blue-700', verified: 'bg-emerald-100 text-emerald-700', locked: 'bg-slate-200 text-slate-700' };

  return (
    <MainLayout>
      <div data-testid="declaration-proof-page" className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Declarations & Proofs</h1>
            <p className="text-sm text-slate-500 mt-1">Tax declarations, Form 12BB workflow, and proof submission</p>
          </div>
          <div className="flex items-center space-x-3">
            <select data-testid="company-selector" value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div className="flex bg-slate-100 rounded-lg p-1" data-testid="view-toggle">
              <button data-testid="view-single-btn" onClick={() => setView('single')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${view === 'single' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>
                Single Employee
              </button>
              <button data-testid="view-bulk-btn" onClick={() => setView('bulk')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1 ${view === 'bulk' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>
                <ListChecks className="w-3.5 h-3.5" /> Bulk Approval
              </button>
            </div>
            {view === 'single' && (
              <select data-testid="employee-selector" value={selectedEmployee} onChange={(e) => setSelectedEmployee(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
                <option value="">Select Employee</option>
                {employees.map((e) => <option key={e.id} value={e.id}>{`${e.personal_details?.first_name} ${e.personal_details?.last_name}`}</option>)}
              </select>
            )}
          </div>
        </div>

        {view === 'bulk' && (
          <div data-testid="bulk-approval-view" className="space-y-4">
            <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium text-slate-700">Status filter:</span>
              {['submitted', 'verified', 'draft', 'locked'].map((s) => (
                <button key={s} data-testid={`bulk-filter-${s}`} onClick={() => setBulkFilter(s)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium ${bulkFilter === s ? 'bg-[#0052FF] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
              <span className="text-xs text-slate-500 ml-auto">{allDecls.length} declarations · {bulkSelected.size} selected</span>
            </div>

            {bulkSelected.size > 0 && (
              <div data-testid="bulk-action-bar" className="bg-[#0052FF]/5 border border-[#0052FF]/30 rounded-xl px-4 py-3 flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-slate-700">{bulkSelected.size} selected →</span>
                {bulkFilter === 'draft' && (
                  <button data-testid="bulk-submit-btn" onClick={() => handleBulkAction('submit')} disabled={bulkBusy}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                    <CheckCheck className="w-3.5 h-3.5" /> Bulk Submit
                  </button>
                )}
                {bulkFilter === 'submitted' && (
                  <button data-testid="bulk-verify-btn" onClick={() => handleBulkAction('verify')} disabled={bulkBusy}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50">
                    <Check className="w-3.5 h-3.5" /> Bulk Verify
                  </button>
                )}
                {bulkFilter === 'verified' && (
                  <button data-testid="bulk-lock-btn" onClick={() => handleBulkAction('lock')} disabled={bulkBusy}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-slate-700 text-white rounded hover:bg-slate-800 disabled:opacity-50">
                    <Lock className="w-3.5 h-3.5" /> Bulk Lock
                  </button>
                )}
                <button onClick={() => setBulkSelected(new Set())} className="ml-auto flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
                  <X className="w-3.5 h-3.5" /> Clear
                </button>
              </div>
            )}

            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-3 py-3 text-center w-10">
                      <input data-testid="bulk-select-all-decls" type="checkbox"
                        checked={allDecls.length > 0 && bulkSelected.size === allDecls.length}
                        onChange={() => {
                          if (bulkSelected.size === allDecls.length) setBulkSelected(new Set());
                          else setBulkSelected(new Set(allDecls.map((d) => d.id)));
                        }} />
                    </th>
                    <th className="px-4 py-3 text-left font-medium">Employee</th>
                    <th className="px-4 py-3 text-left font-medium">FY</th>
                    <th className="px-4 py-3 text-left font-medium">Regime</th>
                    <th className="px-4 py-3 text-left font-medium">Status</th>
                    <th className="px-4 py-3 text-right font-medium"># Sections</th>
                    <th className="px-4 py-3 text-right font-medium">Total Declared</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {allDecls.map((d) => {
                    const total = (d.sections || []).reduce((s, x) => s + (x.declared_amount || 0), 0);
                    return (
                      <tr key={d.id} data-testid={`decl-row-${d.id}`} className={`hover:bg-slate-50 ${bulkSelected.has(d.id) ? 'bg-blue-50/30' : ''}`}>
                        <td className="px-3 py-3 text-center">
                          <input type="checkbox" data-testid={`bulk-select-decl-${d.id}`}
                            checked={bulkSelected.has(d.id)}
                            onChange={() => {
                              const s = new Set(bulkSelected);
                              if (s.has(d.id)) s.delete(d.id); else s.add(d.id);
                              setBulkSelected(s);
                            }} />
                        </td>
                        <td className="px-4 py-3"><p className="font-medium text-slate-800">{employeeName(d.employee_id)}</p><p className="text-xs text-slate-500">{employeeCode(d.employee_id)}</p></td>
                        <td className="px-4 py-3 font-mono text-xs">{d.financial_year}</td>
                        <td className="px-4 py-3 text-xs uppercase text-slate-600">{d.regime}</td>
                        <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColors[d.status] || 'bg-slate-100'}`}>{d.status}</span></td>
                        <td className="px-4 py-3 text-right font-mono text-xs">{(d.sections || []).length}</td>
                        <td className="px-4 py-3 text-right font-mono">{total.toLocaleString()}</td>
                      </tr>
                    );
                  })}
                  {allDecls.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No declarations in {bulkFilter} status</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {view === 'single' && selectedEmployee && (
          <>
            <div className="flex items-center space-x-4 bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex-1 grid grid-cols-3 gap-4">
                <div><label className="block text-xs text-slate-500 mb-1">Financial Year</label>
                  <select data-testid="fy-select" value={fy} onChange={(e) => setFy(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm w-full">
                    {fyDropdownOptions(2, 2).map(label => (
                      <option key={label} value={label}>{label}</option>
                    ))}
                  </select></div>
                <div><label className="block text-xs text-slate-500 mb-1">Tax Regime</label>
                  <select data-testid="regime-select" value={regime} onChange={(e) => setRegime(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm w-full">
                    <option value="new">New Regime</option><option value="old">Old Regime</option>
                  </select></div>
                <div><label className="block text-xs text-slate-500 mb-1">Status</label>
                  <span className={`inline-block px-3 py-2 rounded-lg text-sm font-medium ${statusColors[activeDecl?.status] || 'bg-slate-100'}`}>{activeDecl?.status || 'New'}</span></div>
              </div>
              <div className="flex items-center space-x-2">
                {activeDecl?.status === 'draft' && <button data-testid="submit-decl-btn" onClick={() => handleAction('submit')} className="text-sm bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700">Submit</button>}
                {activeDecl?.status === 'submitted' && <button data-testid="verify-decl-btn" onClick={() => handleAction('verify')} className="text-sm bg-emerald-600 text-white px-3 py-2 rounded-lg hover:bg-emerald-700">Verify</button>}
                {activeDecl?.status === 'verified' && <button data-testid="lock-decl-btn" onClick={() => handleAction('lock')} className="text-sm bg-slate-600 text-white px-3 py-2 rounded-lg hover:bg-slate-700"><Lock className="w-3 h-3 inline mr-1" />Lock</button>}
              </div>
            </div>

            {regime === 'old' ? (
              <div className="space-y-4">
                {/* Section 80C */}
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div><h3 className="text-sm font-semibold text-slate-800">Section 80C</h3><p className="text-xs text-slate-500">Max: 1,50,000 | Declared: {total80C.toLocaleString()}</p></div>
                    <div className="relative group">
                      <button data-testid="add-80c-btn" className="text-xs bg-slate-100 text-slate-600 px-3 py-1.5 rounded-lg hover:bg-slate-200">+ Add 80C</button>
                      <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-10 hidden group-hover:block w-48">
                        {SECTIONS_80C.map((s) => <button key={s} onClick={() => addSection('80C', s)} className="block w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50">{s.replace('_', ' ')}</button>)}
                      </div>
                    </div>
                  </div>
                  {sections.filter(s => s.section === '80C').map((s, i) => {
                    const realIdx = sections.indexOf(s);
                    return (
                      <div key={realIdx} className="flex items-center space-x-3 py-2 border-b border-slate-100 last:border-0">
                        <span className="text-xs text-slate-600 w-32">{s.sub_section?.replace('_', ' ')}</span>
                        <input type="number" value={s.declared_amount} onChange={(e) => updateSection(realIdx, 'declared_amount', parseFloat(e.target.value) || 0)} className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm" placeholder="Declared Amount" />
                        <label className="flex items-center space-x-1"><input type="checkbox" checked={s.proof_submitted} onChange={(e) => updateSection(realIdx, 'proof_submitted', e.target.checked)} /><span className="text-xs">Proof</span></label>
                        <input type="number" value={s.approved_amount} onChange={(e) => updateSection(realIdx, 'approved_amount', parseFloat(e.target.value) || 0)} className="w-24 border border-slate-300 rounded px-2 py-1 text-sm" placeholder="Approved" />
                        <button onClick={() => removeSection(realIdx)} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                      </div>
                    );
                  })}
                </div>

                {/* HRA */}
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                  <h3 className="text-sm font-semibold text-slate-800 mb-3">HRA Exemption</h3>
                  <div className="grid grid-cols-4 gap-3">
                    <div><label className="block text-xs text-slate-600 mb-1">Annual Rent Paid</label><input type="number" value={hra.rent_paid_annual} onChange={(e) => setHra({ ...hra, rent_paid_annual: parseFloat(e.target.value) || 0 })} className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm" /></div>
                    <div><label className="block text-xs text-slate-600 mb-1">City Type</label><select value={hra.city_type} onChange={(e) => setHra({ ...hra, city_type: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm"><option value="metro">Metro</option><option value="non_metro">Non-Metro</option></select></div>
                    <div><label className="block text-xs text-slate-600 mb-1">Landlord Name</label><input value={hra.landlord_name} onChange={(e) => setHra({ ...hra, landlord_name: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm" /></div>
                    <div><label className="block text-xs text-slate-600 mb-1">Landlord PAN</label><input value={hra.landlord_pan} onChange={(e) => setHra({ ...hra, landlord_pan: e.target.value.toUpperCase() })} className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm" /></div>
                  </div>
                </div>

                {/* Other Sections */}
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                  <h3 className="text-sm font-semibold text-slate-800 mb-3">Other Deductions</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {OTHER_SECTIONS.map((os) => {
                      const existing = sections.find(s => s.section === os.code);
                      return (
                        <div key={os.code} className="flex items-center space-x-2">
                          <span className="text-xs text-slate-600 w-36">{os.code} - {os.label} {os.max ? `(Max: ${os.max.toLocaleString()})` : ''}</span>
                          <input type="number" value={existing?.declared_amount || 0}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              if (existing) { updateSection(sections.indexOf(existing), 'declared_amount', val); }
                              else { addSection(os.code, os.label); }
                            }}
                            className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm" />
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* House Property */}
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                  <h3 className="text-sm font-semibold text-slate-800 mb-3">House Property</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="block text-xs text-slate-600 mb-1">Interest on Home Loan (Max 2L)</label><input type="number" value={hp.interest_on_loan} onChange={(e) => setHp({ ...hp, interest_on_loan: parseFloat(e.target.value) || 0 })} className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm" /></div>
                    <div><label className="block text-xs text-slate-600 mb-1">Other Income</label><input type="number" value={otherIncome} onChange={(e) => setOtherIncome(parseFloat(e.target.value) || 0)} className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm" /></div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-base font-semibold text-slate-600">New Tax Regime Selected</h3>
                <p className="text-sm text-slate-400 mt-1">Standard deduction of Rs. 75,000 is automatically applied. No other deductions allowed.</p>
                <div className="mt-4"><label className="block text-xs text-slate-500 mb-1">Other Income (if any)</label>
                  <input type="number" value={otherIncome} onChange={(e) => setOtherIncome(parseFloat(e.target.value) || 0)} className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm w-48 mx-auto" /></div>
              </div>
            )}

            <div className="flex justify-end">
              <button data-testid="save-declaration-btn" onClick={handleSave} className="bg-[#0052FF] text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-[#0040CC]">Save Declaration</button>
            </div>
          </>
        )}

        {view === 'single' && !selectedEmployee && (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-base font-semibold text-slate-600">Select an Employee</h3>
            <p className="text-sm text-slate-400 mt-1">Choose an employee to manage tax declarations and proofs</p>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default DeclarationProof;
